import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage } from './errorMessage';
import { isApiError } from './errors';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: {
      /** Show a toast when this query fails after retries (default false: pages render their own error state). */
      errorToast?: boolean;
    };
    mutationMeta: {
      /** Suppress the global error toast (the caller handles errors, e.g. maps field errors to a form). */
      silent?: boolean;
      /** Toast shown after success (already translated). */
      successMessage?: string;
    };
  }
}

/** Do not retry client errors (4xx); retry transient failures twice. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && (error.isAborted || (error.status >= 400 && error.status < 500))) return false;
  return failureCount < 2;
}

/**
 * Query key conventions (ARCHITECTURE §6.5): keys start with the owning module id —
 * `[moduleId, entity, ...params]`, e.g. `['tasks', 'list', filters]`, `['tasks', 'detail', id]`.
 * Invalidate a whole module or entity with a prefix: `invalidateQueries({ queryKey: ['tasks'] })`.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.errorToast) toast.error(getErrorMessage(error));
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.silent) return;
        if (isApiError(error) && (error.isAborted || error.isValidation)) return;
        toast.error(getErrorMessage(error));
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        if (mutation.meta?.successMessage) toast.success(mutation.meta.successMessage);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}
